import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { Subject } from 'rxjs/Subject';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as bowser from 'bowser';
import * as copy from 'copy-to-clipboard';
import * as moment from 'moment-timezone';
import { DictionaryService } from './dictionary.service';
import { environment } from 'environments/environment';
import { Md5 } from 'ts-md5/dist/md5';
import { Cookie } from 'ng2-cookies/ng2-cookies';

declare function require(moduleName: string): any;

const { version: appVersion } = require('../../../../package.json');

@Injectable()
export class SharedService {
  public appVersion: string;
  public userMessages: Array<any>;
  public targetingLookups: any;
  public activeNetworks: Array<any>;
  public equationOperators: Array<any>;
  public rtbVendors: Array<any>;
  public supplyPartners: Array<any>;
  public demandPartners: Array<any>;
  public jsCallbackDomains: Array<any>;
  public costTypes: Array<any>;
  public domainWhitelist: Array<any>;
  public vendorsList: Array<any>;
  public modalState: boolean;
  public navigateAwayModalState: boolean;
  public navigateAwayConfirmation: boolean;
  public navigateAwayUrl: string;
  public navigateAwayQueryParams: any;
  public modalCedatoState: boolean;
  public modalCedatoHeader: string;
  public modalCedatoBody: string;
  public modalCedatoButton: string;
  public sideMenuState: string;
  public activityLogToggleStatus: boolean;
  public tooltipData: any;
  public tooltipTimeout: any;
  public flashMessages: Array<any>;
  public flashMessagesIndex: number;
  public myAccountMenuToggleStatus: boolean;
  public layout: string;
  public monthsNames: Array<string>;
  public daysNames: Array<string>;
  public lowercaseWords: Array<string>;
  public uppercaseWords: Array<string>;
  public messagesBus: Subject<any>;
  public offsetEST: number;
  public scrollBarWidth: number;
  public envTypes: Array<any>;
  public isDynamicRoutesLoaded: boolean;
  public changeNetworkTo: any;
  public clientTypeByServer: string;
  public environment: any;
  public saveQueryParamsOnNetworkChange: boolean;
  public formatJSONstring: string;
  public entityTypeSupply: number;
  public entityTypeDemand: number;
  public globalPrebidUrl: string;

  constructor(
    public title: Title,
    public location: Location,
    public router: Router,
    public dictionaryService: DictionaryService
  ) {
    this.appVersion = appVersion;
    this.userMessages = [];
    this.equationOperators = ['=', '<', '>', '≤', '≥'];
    this.supplyPartners = [];
    this.demandPartners = [];
    this.jsCallbackDomains = [];
    this.costTypes = [];
    this.domainWhitelist = [];
    this.vendorsList = [];
    this.rtbVendors = [];
    this.modalState = false;
    this.navigateAwayModalState = false;
    this.navigateAwayConfirmation = true;
    this.navigateAwayUrl = '';
    this.navigateAwayQueryParams = {};
    this.modalCedatoState = false;
    this.modalCedatoHeader = '';
    this.modalCedatoBody = '';
    this.modalCedatoButton = '';
    this.sideMenuState = 'big';
    this.activityLogToggleStatus = false;
    this.tooltipData = { visible: false, x: -100, y: -100 };
    this.flashMessages = [];
    this.flashMessagesIndex = 0;
    this.myAccountMenuToggleStatus = false;
    this.monthsNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    this.daysNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    this.lowercaseWords = [
      'a',
      'an',
      'the',
      'at',
      'by',
      'for',
      'in',
      'of',
      'on',
      'to',
      'up',
      'and',
      'as',
      'but',
      'or',
      'nor',
    ];
    this.uppercaseWords = ['VAST', 'URL', 'CPM', 'JSON'];
    this.messagesBus = new Subject<any>();
    this.offsetEST = -300;
    this.scrollBarWidth = null;
    this.envTypes = [];
    this.isDynamicRoutesLoaded = false;
    this.changeNetworkTo = {};
    this.clientTypeByServer = 'dashboard';
    this.environment = environment;
    this.saveQueryParamsOnNetworkChange = false;
    this.formatJSONstring = '    ';
    this.entityTypeSupply = 2;
    this.entityTypeDemand = 3;
    this.globalPrebidUrl = '';
  }

  setPageTitle(page: string = null, entityName: string = null) {
    let title: string = this.dictionaryService.get('CEDATO_DASHBOARD');
    if (page) {
      title += ' | ' + this.dictionaryService.get(page);
    }
    if (entityName) {
      title += ' - ' + entityName;
    }
    this.title.setTitle(title);
  }

  toggleSideMenu(state: string = null) {
    if (state !== null) {
      this.sideMenuState = state;
    } else {
      if (this.layout === 'large') {
        this.sideMenuState = this.sideMenuState === 'big' ? 'small' : 'big';
      } else {
        this.sideMenuState = this.sideMenuState === 'big' ? 'closed' : 'big';
      }
    }

    this.tooltipData.visible = false;
    this.sendMessage('SIDE_MENU_STATE_CHANGE', this.sideMenuState);
    localStorage.setItem('sideMenuState', this.sideMenuState);
  }

  stringToESTDate(dateString: string = '') {
    return moment(dateString).utc().utcOffset(this.offsetEST).toDate();
  }

  toggleActivityLog() {
    this.activityLogToggleStatus = !this.activityLogToggleStatus;
    this.activityLogToggleStatus ? this.sendMessage('ACTIVITY_LOG_SHOW') : this.sendMessage('ACTIVITY_LOG_HIDE');
  }

  toggleDocumentScroll(state: boolean) {
    document.getElementsByTagName('html')[0].style.overflowY = state ? 'auto' : 'hidden';
  }

  showTooltip(
    title: string,
    element: HTMLElement,
    editable: boolean = false,
    centered: boolean = false,
    forceTop: boolean = false
  ) {
    const letterSpacing = 3;
    let toolTipMarginLeft = 0;
    const toolTipMarginTop: number = document.getElementsByTagName('body')[0].scrollTop;
    let triangleMarginLeft = 8;
    const triangleMarginTop = 0;
    let triangleAtTop = true;
    const x: number = element.getBoundingClientRect().left;
    let y: number = element.getBoundingClientRect().top;
    if (!forceTop) {
      if (x > window.innerWidth * 0.8) {
        toolTipMarginLeft = title.length === 0 ? 0 : title.length * letterSpacing * -1;
        triangleMarginLeft -= toolTipMarginLeft;
      }
      if (y > window.innerHeight * 0.8) {
        y = element.getBoundingClientRect().bottom;
        triangleAtTop = false;
      }
    } else {
      y -= 30;
      triangleAtTop = false;
    }

    this.tooltipData = {
      visible: true,
      title: title,
      x: x + toolTipMarginLeft,
      y: y + toolTipMarginTop,
      element: element,
      triangleX: triangleMarginLeft,
      triangleY: triangleMarginTop,
      triangleAtTop: triangleAtTop,
      editable: editable,
      parentWidth: centered ? element.getBoundingClientRect().width : 'auto',
      centered: centered,
    };
    this.sendMessage('SHOW_TOOLTIP', title);
  }

  hideTooltip() {
    this.tooltipData.visible = false;
    clearTimeout(this.tooltipTimeout);
  }

  showNavigateAwayModal() {
    this.navigateAwayModalState = true;
    this.navigateAwayConfirmation = false;
    this.toggleDocumentScroll(true);
  }

  hideNavigateAwayModal() {
    this.navigateAwayModalState = false;
    this.toggleDocumentScroll(true);
  }

  showCedatoModal() {
    this.modalCedatoState = true;
    this.toggleDocumentScroll(true);
  }

  hideCedatoModal() {
    this.modalCedatoState = false;
    this.toggleDocumentScroll(true);
  }

  showFlashMessage(
    content: string,
    type: string,
    autoDismiss: boolean = true,
    closeButton: boolean = true,
    broadcastId: number = null
  ) {
    let message: any;
    if (_.find(this.flashMessages, { content: content, type: type }) === undefined) {
      message = {
        id: this.flashMessagesIndex,
        content: content,
        type: type,
        closeButton: closeButton,
        broadcastId: broadcastId,
        state: 'in',
      };
      this.flashMessages.push(message);
      this.flashMessagesIndex++;
      if (autoDismiss) {
        this.hideFlashMessage(message, 5000);
      }
      this.sendMessage('SHOW_FLASH_MESSAGE', { type: type, content: content });
    }
  }

  hideFlashMessage(message: any, timeout: number = 0) {
    setTimeout(() => {
      const index: number = _.findIndex(this.flashMessages, (o: any) => {
        return o.id === message.id;
      });
      message.state = 'out';
      if (message.broadcastId) {
        this.sendMessage('BROADCAST_DISMISS', message.broadcastId);
      }
      setTimeout(() => {
        this.flashMessages.splice(index, 1);
      }, 400);
    }, timeout);
  }

  hashString(str: string): string {
    const hasher = new Md5();
    hasher.appendStr(str);
    return hasher.end().toString();
  }

  deleteAllCookies() {
    Cookie.deleteAll();
  }

  showErrorFlashMessage(errors: any = null) {
    let messages: Array<string> = [];
    if (errors) {
      if (_.isObject(errors)) {
        for (const error in errors) {
          if (errors[error] !== 'VALID') {
            if (typeof errors[error] === 'string') {
              messages.push(this.getErrorName(error) + this.getErrorPostfix(errors[error]));
            } else if (_.isArray(errors[error])) {
              errors[error].forEach((innerError: any) => {
                for (const innerErrorKey in innerError) {
                  messages.push(this.getErrorName(innerErrorKey) + this.getErrorPostfix(innerError[innerErrorKey]));
                }
              });
            }
          }
        }
        messages = _.uniq(messages);
        for (let i = 0; i < messages.length; i++) {
          this.showFlashMessage(messages[i], 'error');
        }
      } else {
        if (_.isString(errors) && errors.length) {
          this.showFlashMessage(errors, 'error');
        }
      }
    }
    if (this.isEmpty(errors) || messages.length === 0) {
      this.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
    }
  }

  getErrorName(error: string) {
    const currKey: string = this.fixPhrasing(this.fixCase(error.split('_').join(' ')));
    return this.isNumeric(currKey) ? '' : currKey + ' - ';
  }

  getErrorPostfix(error: string) {
    let postfix: string;
    let parts: Array<string>;
    switch (error) {
      case 'NOT_VALID_BY_CLIENT':
      case 'INVALID':
        postfix = this.dictionaryService.get('MISSING_OR_INVALID');
        break;

      case 'NOT_SUPPORTED':
        postfix = this.dictionaryService.get('NOT_SUPPORTED');
        break;

      case String(error.match(/^EXCEEDED_MAX_CHARS_SIZE.*/)):
        parts = error.split(':');
        postfix = this.dictionaryService.get(parts[0])(parts[1]);
        break;

      case 'MAX_LENGTH_PUBLISHER_NAME':
        postfix = this.dictionaryService.get('PUBLISHER_NAME_IS_TOO_LONG');
        break;

      default:
        postfix = this.fixPhrasing(error);
    }

    return postfix;
  }

  getIndexedArray(array: Array<any>, indexField: string) {
    const resultArray = {};
    array.forEach((one, index) => {
      one._index = index;
      resultArray[one[indexField]] = one;
    });
    return resultArray;
  }

  sendMessage(type: string, data: any = null) {
    const style = 'font-weight: bold';
    const log = false;
    this.messagesBus.next({ type: type, data: data });
    if (!environment.production && log) {
      if (data) {
        console.group(type);
        console.log(data);
        console.groupEnd();
      } else {
        console.log('%c' + type, style);
      }
    }
  }

  getMessagesBus(): Observable<any> {
    return this.messagesBus.asObservable();
  }

  onEntitySaveFailure() {
    this.showFlashMessage(this.dictionaryService.get('NEW_ENTITY_GENERAL_ERROR'), 'error');
    setTimeout(() => {
      const errors: Array<any> = Array.from(document.getElementsByClassName('formErrorActive'));
      let block: any = null;
      let currElement: any;
      if (errors.length > 0) {
        currElement = errors[0];
        if (currElement.parentNode) {
          if (currElement.parentNode.parentNode) {
            if (Array.from(currElement.parentNode.parentNode.classList).includes('sectionContent')) {
              block = currElement.parentNode.parentNode;
            }
          }
        }
        while (block === null && currElement.parentNode !== undefined) {
          currElement = currElement.parentNode;
          if (currElement.classList) {
            if (Array.from(currElement.classList).includes('block')) {
              block = currElement;
            }
          }
        }
        window.scroll({ top: block.offsetTop, left: 0, behavior: 'smooth' });
      }
    }, 100);
  }

  copyToClipboard(val: any, cell: string = '', value: string = '') {
    let valueToCopy = '';
    if (cell === 'environment') {
      const dictionaryService: DictionaryService = new DictionaryService();
      let envName = '';
      if (value) {
        switch (value) {
          case '1':
            envName = 'DESKTOP';
            break;
          case '2':
            envName = 'MOBILE_WEB';
            break;
          case '3':
            envName = 'MOBILE_APP';
            break;
          case '4':
            envName = 'CTV';
            break;
          default:
            envName = '';
        }
      }
      valueToCopy = dictionaryService.get(envName);
    } else {
      valueToCopy = val;
    }
    copy(valueToCopy);
    this.showFlashMessage(this.dictionaryService.get('COPIED_TO_CLIPBOARD'), 'info');
  }

  back(excludedURLs: Array<any> = [], defaultURL: string = '/') {
    let oldURL: string = localStorage.getItem('oldURL');
    const oldURLParts: Array<string> = oldURL.split('/');
    let toDefault: boolean = this.isEmpty(oldURL);
    if (this.isNumeric(oldURLParts[oldURLParts.length - 1])) {
      oldURLParts.splice(oldURLParts.length - 1, 1);
    }
    oldURL = oldURLParts.join('/');
    excludedURLs.forEach((url: string) => {
      if (oldURL.includes(url.replace('*', ''))) {
        toDefault = true;
      }
    });
    if (toDefault) {
      this.router.navigate([defaultURL]);
    } else {
      this.location.back();
    }
  }

  padNumber(n: number) {
    let s: string = n.toString();
    if (n < 10) {
      s = '0' + s;
    }
    return s;
  }

  roundUp(n: number, allowDecimal: boolean) {
    let n2: number = n;
    let a = 1;
    let s: string;
    if (n < 1) {
      n2 = n;
    } else if (n >= 1 && n < 2) {
      n2 = 2;
    } else if (n >= 2 && n < 2.5) {
      n2 = 2.5;
      if (!allowDecimal) {
        n2 = 2;
      }
    } else if (n >= 2 && n < 4) {
      n2 = 4;
    } else if (n >= 4 && n < 5) {
      n = 5;
    } else if (n >= 5 && n < 10) {
      n2 = 10;
    } else {
      n2 = Math.round(n);
      s = '';
      for (let i = 0; i < n2.toString().length; i++) {
        if (i === 0) {
          s += n2.toString().charAt(i);
        } else {
          s += '0';
          a *= 10;
        }
      }
      if (parseInt(n2.toString().charAt(1)) < 5) {
        a /= 2;
      }
      n2 = parseInt(s) + a;
    }
    if (!allowDecimal) {
      n2 = Math.round(n2);
      if (n2 / 2 !== Math.round(n2 / 2)) {
        n2++;
      }
    }
    return n2;
  }

  toLocaleString(n: any, min: number = 0, max: number = 0) {
    try {
      n = parseFloat(n);
      return n.toLocaleString(undefined, { minimumFractionDigits: min, maximumFractionDigits: max });
    } catch (error) {
      return n;
    }
  }

  strToInt(value: string) {
    let num = '';
    if (!value || !value.length) { return null; }

    for (let i = 0; i < value.length; i++) {
      if (this.isNumeric(value.charAt(i))) {
        num += value.charAt(i);
      }
    }
    return this.isNumeric(num) ? parseInt(num) : null;
  }

  getNumOfKeys(obj: any) {
    return obj ? Object.keys(obj).length : 0;
  }

  isEmpty(value: any) {
    let answer = true;
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        answer = value.length === 0;
      } else if (value instanceof Object) {
        answer = this.getNumOfKeys(value) === 0;
      } else {
        answer =
          value === null || value.toString().split(' ').join('') === '' || value === 'undefined' || value === 'NaN';
      }
    }
    return answer;
  }

  isEmptyObj(value: any) {
    let answer = true;
    if (value instanceof Object) {
      answer = Object.keys(value).every(function (x) {
        return value[x] === null;
      });
    }
    return answer;
  }

  trackBy(item: any) {
    return item.index;
  }

  validateURL(value: string) {
    const urlRegex: any =
      /^(?:http:\/\/|https:\/\/|\/\/)(?:(?:[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?\.)+(?:[A-Z]{2,6}\.?|[A-Z0-9-]{2,}\.?)|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?(?:(.*?))$/i;
    return (urlRegex.test(value) || urlRegex.test('http://' + value)) && value.indexOf(`\\`) === -1;
  }

  validateEmail(value: string) {
    const emailRegex: any =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(value);
  }

  validateFilename(value: string) {
    const invalidChars: any = /[\/:*?"<>|]/;
    return !invalidChars.test(value) && value.indexOf('\\') === -1;
  }

  validateUsername(value: string) {
    if (this.isEmpty(value)) {
      return false;
    }
    return value.length > 2 && value.length < 40;
  }

  validatePassword(value: string) {
    const passwordRegex: any = /[A-Z]+/;
    if (this.isEmpty(value)) {
      return false;
    }
    return value.length >= 8 && passwordRegex.test(value);
  }

  getBrowser() {
    return bowser;
  }

  isMobile() {
    const answer: any =
      navigator.userAgent.match(/Android/i) ||
      navigator.userAgent.match(/webOS/i) ||
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPod/i) ||
      navigator.userAgent.match(/BlackBerry/i) ||
      navigator.userAgent.match(/Windows Phone/i);
    return answer !== null;
  }

  fixCase(value: string) {
    const words: Array<string> = !this.isEmpty(value) ? value.split(' ') : [];
    const fixedWords: Array<string> = [];
    let currWord: string;
    for (let i = 0; i < words.length; i++) {
      currWord = words[i].toLowerCase();
      if (this.lowercaseWords.includes(currWord)) {
        currWord = currWord.toLowerCase();
      } else if (this.uppercaseWords.includes(currWord.toUpperCase())) {
        currWord = currWord.toUpperCase();
      } else {
        if (currWord.charAt(0) !== '(') {
          currWord = currWord.charAt(0).toUpperCase() + currWord.substr(1, currWord.length - 1);
        } else {
          currWord = '(' + currWord.charAt(1).toUpperCase() + currWord.substr(2, currWord.length - 1);
        }
      }
      fixedWords.push(currWord);
    }
    return fixedWords.join(' ');
  }

  fixPhrasing(str: string) {
    str = str.replace('Player Name', 'Supply Name');
    str = str.replace('Vast Name', 'Demand Name');
    str = str.replace('VAST Name', 'Demand Name');
    return str;
  }

  sortFuncForIntArr(small: number, big: number) {
    if (small === big) {
      return 0;
    } else if (small < big) {
      return -1;
    } else if (small > big) {
      return 1;
    }
  }

  validateIP(ip: string): boolean {
    if (
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
        ip.trim()
      )
    ) {
      return true;
    }
    return false;
  }

  generateRandomNumber(maxLength: number): number {
    let randomNumberString = '';
    for (let i = 0; i < maxLength; i++) {
      randomNumberString += Math.floor(Math.random() * 10);
    }
    return +randomNumberString;
  }

  generateUniqString(maxLength: number): string {
    const charsArr: string[] = ['¤', '×', '÷', '§', '¦', 'µ', '¹', 'ª', 'º', '¢'];
    let randomString = '';
    for (let i = 0; i < maxLength; i++) {
      randomString += charsArr[Math.floor(Math.random() * charsArr.length)];
    }
    return randomString;
  }

  fastStringArrFilter(
    searchText: string,
    searchArray: Array<string>,
    searchField: string,
    isCaseSensitive: boolean = true
  ) {
    let tempSearchArray = searchArray;
    const charArray = searchText.split('');
    let currentSearchText = '';
    for (const letter of charArray) {
      currentSearchText += letter;
      tempSearchArray = tempSearchArray.filter((word) => {
        if (isCaseSensitive) {
          if (searchField) {
            return word[searchField].includes(currentSearchText);
          } else {
            return word.includes(currentSearchText);
          }
        } else {
          if (searchField) {
            return word[searchField].toLowerCase().includes(currentSearchText.toLowerCase());
          } else {
            return word.toLowerCase().includes(currentSearchText.toLowerCase());
          }
        }
      });
      if (tempSearchArray.length == 0) {
        //No need to continue, everything is already eleminated.
        break;
      }
    }
    return tempSearchArray;
  }

  getEnvironmentString(is_desktop: boolean, is_mobile_web: boolean, is_mobile_app: boolean, is_ctv: boolean = false) {
    const keywords: Array<string> = [];
    if (is_desktop && is_mobile_web && is_mobile_app) {
      return this.dictionaryService.get('ALL');
    } else if (!(is_desktop || is_mobile_web || is_mobile_app || is_ctv)) {
      return this.dictionaryService.get('NONE');
    } else {
      if (is_desktop) {
        keywords.push(this.dictionaryService.get('DESKTOP'));
      }
      if (is_mobile_web) {
        keywords.push(this.dictionaryService.get('MOBILE_WEB'));
      }
      if (is_mobile_app) {
        keywords.push(this.dictionaryService.get('MOBILE_APP'));
      }
      if (is_ctv) {
        keywords.push(this.dictionaryService.get('CTV'));
      }
      return keywords.join(', ');
    }
  }

  isNumeric(value: any) {
    const isANumberRegex: RegExp = new RegExp(/^[+-]?\d+(\.\d+)?$/);
    if (this.isEmpty(value)) {
      return false;
    } else {
      value = value.toString();
      value = value.replace('%', '');
      value = value.replace('$', '');
      value = value.replace(',', '');
      return isANumberRegex.test(value);
    }
  }

  parseBoolean(val: any) {
    return val && (val.toString() === 'true' || val.toString() === '1');
  }

  getMouseCoords($event: any, axis: string) {
    const isMouse: boolean = $event.type.indexOf('mouse') > -1;
    if (axis === 'x') {
      return isMouse ? $event.clientX : $event.changedTouches[0].clientX;
    } else if (axis === 'y') {
      return isMouse ? $event.clientY : $event.changedTouches[0].clientY;
    }
    return 0;
  }

  composedPath(el: any) {
    const path: Array<any> = [];
    while (el) {
      path.push(el);
      if (el.tagName === 'HTML') {
        path.push(document);
        path.push(window);
        return path;
      }
      el = el.parentElement;
    }
  }

  boldHighlight(fullText: string, query: string) {
    let highlightedText: string = fullText;
    const startIndex: number = fullText.toLowerCase().indexOf(query.toLowerCase());
    if (startIndex > -1) {
      highlightedText =
        `<span>` +
        highlightedText.substr(0, startIndex) +
        `</span><span class='bold'>` +
        highlightedText.substr(startIndex, query.length) +
        `</span><span>` +
        highlightedText.substr(startIndex + query.length, highlightedText.length - (startIndex + query.length)) +
        `</span>`;
    }
    return highlightedText;
  }

  clearLocalStorage() {
    for (const item in localStorage) {
      localStorage.removeItem(item);
    }
  }

  getScrollbarWidth() {
    let outer: HTMLElement;
    let inner: HTMLElement;
    let widthNoScroll: number;
    let widthWithScroll: number;
    if (this.scrollBarWidth === null) {
      outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.width = '100px';
      outer.style.msOverflowStyle = 'scrollbar';
      document.body.appendChild(outer);
      widthNoScroll = outer.offsetWidth;
      outer.style.overflow = 'scroll';
      inner = document.createElement('div');
      inner.style.width = '100%';
      outer.appendChild(inner);
      widthWithScroll = inner.offsetWidth;
      outer.parentNode.removeChild(outer);
      this.scrollBarWidth = widthNoScroll - widthWithScroll;
    }
    return this.scrollBarWidth;
  }

  abbreviateNumber(num: number) {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  }

  parseNumeric(n: any) {
    return this.isEmpty(n) ? n : parseFloat(n);
  }

  isJSON(str: any) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  isURLParams(str: string) {
    const urlParamsRegex: any = /^((\?[\w-]+(=[,.\w-]+)|&[\w-]+(=[,.\w-]+)|[\w-]+(=[,.\w-]+))?(&[\w-]+(=[,.\w-]+))*)?$/i;
    return urlParamsRegex.test(str);
  }

  flattenObj(obj, parent = null, res = {}) {
    for (const key in obj) {
      const propName = parent ? parent + '.' + key : key;
      if (typeof obj[key] === 'object') {
        this.flattenObj(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }
    return res;
  }

  cleanString(str: string, removedSubstrings: Array<string>) {
    let str2: string = str;
    removedSubstrings.forEach((item: string) => {
      while (str2.indexOf(item) > -1) {
        str2 = str2.replace(item, '');
      }
    });
    return str2;
  }

  deepDiff(object: any, base: any) {
    const changes: Function = (object: any, base: any) => {
      return _.transform(object, (result: any, value: any, key: string) => {
        if (!_.isEqual(value, base[key])) {
          result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
        }
      });
    };
    return changes(object, base);
  }

  checkResponseStatus(response: any) {
    return response && response.status && response.status.toLowerCase() === 'ok';
  }

  daysList(startDate, endDate) {
    const dateTimeEST: any = moment().utcOffset(this.offsetEST);
    if (_.isUndefined(startDate)) { startDate = dateTimeEST.format('YYYY-MM-DD'); }

    if (_.isUndefined(endDate)) { endDate = dateTimeEST.format('YYYY-MM-DD'); }

    const dateArray = [],
      ed = moment(endDate);
    let sd = moment(startDate);
    while (sd <= ed) {
      dateArray.push(moment(sd).format('YYYY-MM-DD'));
      sd = moment(sd).add(1, 'days');
    }
    return dateArray;
  }

  stringToQueryParams(obj: any) {
    return (
      '?' +
      Object.keys(obj)
        .map((key) => [key, obj[key]].join('='))
        .join('&')
    );
  }

  clearLocalStorageGridFilters() {
    let key: Array<string>;
    const removedItems: Array<any> = [];
    for (const item in localStorage) {
      key = item.split('-');
      if (key.length === 3 && key[0] === 'grid' && key[2] === 'filters') {
        removedItems.push(item);
      }
    }
    removedItems.forEach((item: string) => {
      localStorage.removeItem(item);
    });
  }

  versionCompare(v1: string, v2: string, options: any = {}) {
    const lexicographical = options && options.lexicographical,
      zeroExtend = options && options.zeroExtend;
    let v1parts: Array<any> = v1.split('.'),
      v2parts: Array<any> = v2.split('.');

    function isValidPart(x) {
      return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
    }

    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
      return NaN;
    }

    if (zeroExtend) {
      while (v1parts.length < v2parts.length) { v1parts.push('0'); }
      while (v2parts.length < v1parts.length) { v2parts.push('0'); }
    }

    if (!lexicographical) {
      v1parts = v1parts.map(Number);
      v2parts = v2parts.map(Number);
    }

    for (let i = 0; i < v1parts.length; ++i) {
      if (v2parts.length == i) {
        return 1;
      }

      if (v1parts[i] == v2parts[i]) {
        continue;
      } else if (v1parts[i] > v2parts[i]) {
        return 1;
      } else {
        return -1;
      }
    }

    if (v1parts.length != v2parts.length) {
      return -1;
    }

    return 0;
  }
}
